import { useIsFocused } from "expo-router/react-navigation";
import { router, useLocalSearchParams } from 'expo-router';
import { capitalize } from 'lodash';
import { useEffect, useLayoutEffect } from 'react';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import { Divider } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useNpoOnboarding } from '@/api/hooks/meHooks';
import { useGetNonProfitById } from '@/api/hooks/nonProfitOrgHooks';
import { useCreateManySalaries } from '@/api/hooks/salariesHooks';
import { NonProfitOrg } from '@/api/models/nonProfitOrgModels';
import AddAnotherButton from '@/components/buttons/AddAnotherButton';
import BrandedButton from '@/components/buttons/BrandedButton';
import FormWrapper from '@/components/forms/FormWrapper';
import SalaryForm from '@/components/forms/SalaryForm';
import FieldArrayListItemCard from '@/components/npo/onboarding/FieldArrayListItemCard';
import OnboardingSection from '@/components/npo/onboarding/OnboardingSection';
import SaveAndContinueLaterButton from '@/components/npo/onboarding/SaveAndContinueLaterButton';
import Text from '@/components/Text';
import { useAuth } from '@/contexts/authContext';
import { useNpoLoginScreenState } from '@/contexts/npoLoginScreenStateContext';
import { SalariesData, salariesResolver, SalaryData } from '@/lib/validations/NpoOnboardingSchema';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';
import { objectComparision } from '@/utils/objectComparision';

const Salaries = () => {
  const { salaryDataJson, salaryIndex } = useLocalSearchParams<{
    salaryDataJson: string;
    salaryIndex: string;
  }>();
  const { state: authState } = useAuth();
  const { state: npoScreensState, setCurrentPage } = useNpoLoginScreenState();
  const { mutateAsync: onBoardingUpdate, isPending: onBoardingIsPending } = useNpoOnboarding(
    authState.signUpData?.loginEmail?.type ?? 'CHURCH'
  );
  const { mutateAsync: createManySalaries, isPending: salariesPending } = useCreateManySalaries();
  const { data: npoData } = useGetNonProfitById({ id: authState.signUpData?.loginEmail?.npoId ?? '' });
  const { height } = useWindowDimensions();
  const { top, bottom } = useSafeAreaInsets();

  const isFocused = useIsFocused();
  useLayoutEffect(() => {
    if (isFocused) setCurrentPage(10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused]);

  useEffect(() => {
    if (salaryDataJson) {
      const salaryData = safeJsonParse<SalaryData>(salaryDataJson);
      if (salaryData && fields !== undefined) {
        // eslint-disable-next-line no-unused-expressions
        !salaryIndex && !fields.find((field) => objectComparision(field, salaryData))
          ? append(salaryData)
          : update(+salaryIndex, salaryData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salaryDataJson]);

  const nonProfitOrg = safeJsonParse<NonProfitOrg>(npoScreensState.initialNpoData ?? '');

  const isIndividual = authState.authReady && authState.user?.user.role === 'INDIVIDUAL';
  const isChurch = authState.signUpData?.loginEmail?.type === 'CHURCH';

  const {
    control,
    handleSubmit,
    trigger,
    getValues,
    getFieldState,
    watch,
    setError,
    clearErrors,
    formState: { isValid, submitCount, errors }
  } = useForm<SalariesData>({
    defaultValues: {
      salaries:
        nonProfitOrg?.salaries !== undefined ? safeJsonParse<SalaryData[]>(JSON.stringify(nonProfitOrg.salaries)) : []
    },
    mode: 'onSubmit',
    resolver: salariesResolver,
    context: { isIndividual }
  });

  const { fields, insert, append, update, remove } = useFieldArray({
    control,
    name: 'salaries'
  });

  const salariesWatched = watch('salaries');
  const minimalSalaries = Math.min(Math.trunc((npoData?.totalEmployees ?? 0) / 10), 10);

  useEffect(() => {
    if (salariesWatched.length >= minimalSalaries) {
      clearErrors('salaries');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salariesWatched]);

  const onSubmit: SubmitHandler<SalariesData> = async (data) => {
    if (salariesWatched.length < minimalSalaries && minimalSalaries > 1) {
      setError('salaries', {
        message: i18n.t('sign_up.nonProfit.wizard.errors.salaries.minimal', {
          minimal_salaries: minimalSalaries
        })
      });
    } else {
      const mappedData = data.salaries.map((salary) => ({
        ...salary,
        nonProfitOrgId: authState.signUpData?.loginEmail?.npoId ?? ''
      }));
      const body = {
        salaries: mappedData,
        npoId: authState.signUpData?.loginEmail?.npoId
      };

      try {
        createManySalaries(body, {
          onSuccess: () => {
            onBoardingUpdate(
              {
                preferenceId: authState.preferenceId ?? '',
                salaries: true
              },
              {
                onSuccess: () => {
                  router.navigate('/(app)/login/nonProfit/onBoarding/landEquipments');
                }
              }
            );
          },
          onError: (e) => {
            console.error(e);
          }
        });
      } catch (e) {
        console.error(e);
      }
    }
  };

  return (
    <FormWrapper scrollEnable minHeightMinus={160}>
      {/* Height - Header */}
      <View style={styles.container}>
        <OnboardingSection
          label={i18n.t('sign_in.nonProfit.salaries.salaries_section')}
          description={i18n.t('sign_in.nonProfit.salaries.enter_at_least')}>
          {getValues('salaries') && getValues('salaries').length === 0 ? (
            // height - header - footer
            <View style={{ height: height - 160 - bottom - top }}>
              <SalaryForm
                initialData={getValues('salaries.0')}
                showDoneButton
                showBackButton
                isLoading={onBoardingIsPending || salariesPending}
                manualValidation={isChurch}
                onDone={(data, triggerSalary) => {
                  if (!data.fullName && !data.position && !getFieldState('salaries').isDirty) {
                    if (isChurch) {
                      clearErrors();
                      handleSubmit(onSubmit)();
                    }
                    return;
                  }
                  if (data.fullName && data.position) {
                    clearErrors();
                    insert(0, data);
                    if (minimalSalaries <= 1) {
                      clearErrors('salaries');
                      handleSubmit(onSubmit)();
                    }
                  } else if (!data.fullName || !data.position || data.allowance || data.annualSalary) {
                    triggerSalary();
                  }
                }}
                onAdd={(data) => {
                  update(0, data);
                  trigger('salaries.0').then((isValid) => {
                    if (isValid) {
                      router.navigate('/login/nonProfit/addUpdateSalary');
                    }
                  });
                }}
                doneLabel={i18n.t('mix.next')}
              />
            </View>
          ) : (
            <View style={styles.flexOne}>
              <Divider style={styles.divider} />
              <FlatList
                style={styles.flexOne}
                contentContainerStyle={styles.flexOne}
                scrollEnabled={false}
                data={fields}
                keyExtractor={(salary) => salary.id}
                renderItem={(field) => (
                  <FieldArrayListItemCard
                    onPress={() => {
                      router.navigate({
                        pathname: '/login/nonProfit/addUpdateSalary',
                        params: {
                          salaryDataJson: JSON.stringify(field.item),
                          salaryIndex: field.index
                        }
                      });
                    }}
                    onDelete={() => {
                      remove(field.index);
                    }}
                    title={field.item.fullName}
                    subtitle={field.item.position}
                    budget={field.item.annualSalary}
                  />
                )}
                ListFooterComponent={
                  <AddAnotherButton
                    onPress={() => router.navigate('/login/nonProfit/addUpdateSalary')}
                    label={i18n.t('sign_in.nonProfit.salaries.add_another')}
                  />
                }
              />
            </View>
          )}
        </OnboardingSection>

        <View style={styles.buttonsContainer}>
          {errors.salaries && <Text style={styles.errorText}>{errors?.salaries?.message}</Text>}
          {getValues('salaries') && getValues('salaries').length > 0 && (
            <View style={styles.nextBackContainer}>
              {router.canGoBack() && (
                <BrandedButton
                  disabled={onBoardingIsPending || salariesPending}
                  style={styles.flexOne}
                  onPress={router.back}>
                  {capitalize(i18n.t('mix.back'))}
                </BrandedButton>
              )}
              <BrandedButton
                style={styles.flexOne}
                loading={onBoardingIsPending || salariesPending}
                disabled={!isValid && submitCount > 0}
                onPress={handleSubmit(onSubmit)}>
                {i18n.t('mix.next')}
              </BrandedButton>
            </View>
          )}
          <SaveAndContinueLaterButton disabled={onBoardingIsPending || salariesPending} />
        </View>
      </View>
    </FormWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between'
  },

  flexOne: {
    flex: 1
  },

  nextBackContainer: {
    flex: 1,
    flexDirection: 'row',
    width: '100%',
    gap: 10
  },

  buttonsContainer: {
    flexDirection: 'column'
  },

  errorText: {
    color: 'red',
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10
  },

  divider: {
    marginBottom: 15
  }
});

export default Salaries;
